// How far into the aside's own left column a downstream slice's content must
// reach before we count it as full-width and pin the sidebar there. Small
// enough to catch true full-bleed sections, large enough to ignore layouts
// that only nudge left for optical alignment.
const FULL_WIDTH_INTRUSION_PX = 80;

// Every slice on the clients page wraps its content in the same
// `mx-auto max-w-[1280px]` container, so measuring that wrapper always reads
// as full-width. Instead, walk the real leaf-ish content nodes (typography,
// media, controls) inside the section and take the smallest `left` — that
// tells us where the section's *visible* content actually starts. Right-column
// layouts intentionally offset their content past the aside's rail; full-width
// ones don't.
const CONTENT_LEAF_SELECTOR =
  'h1, h2, h3, h4, h5, h6, p, li, blockquote, figure, img, svg, video, picture, button, a, input, textarea, select, label';

/**
 * Smallest `getBoundingClientRect().left` among the section's visible content
 * leaves — text, media, controls. Returns `null` when the section has no
 * measurable content (all hidden, empty section, etc.).
 */
export function computeLeftmostContentX(section: HTMLElement): number | null {
  const candidates = section.querySelectorAll<HTMLElement>(
    CONTENT_LEAF_SELECTOR,
  );
  let min = Number.POSITIVE_INFINITY;
  candidates.forEach((el) => {
    const rect = el.getBoundingClientRect();
    // Skip elements that aren't laying out anything visible — hidden nodes,
    // ARIA-only spacers, or zero-size icons — so a stray hidden <span> in a
    // right-column slice can't wrongly flag it as full-width.
    if (rect.width < 4 || rect.height < 4) {
      return;
    }
    if (rect.left < min) {
      min = rect.left;
    }
  });
  return Number.isFinite(min) ? min : null;
}

/**
 * Walks the CaseStudyChallenge section's downstream siblings and returns the
 * first one whose visible content encroaches on the aside's own left column.
 * Everything else — right-only content, narrow single-column blocks — is
 * considered compatible with a pinned aside and gets skipped.
 *
 * Returns `null` when no full-width slice appears before the page runs out,
 * meaning the sticky rail can stay fixed all the way down.
 */
export function findFullWidthStopper(
  challengeSection: HTMLElement,
  asideRect: DOMRect,
): HTMLElement | null {
  if (asideRect.width <= 0) {
    return null;
  }
  const asideRight = asideRect.right;
  let node = challengeSection.nextElementSibling as HTMLElement | null;

  while (node) {
    const leftmost = computeLeftmostContentX(node);
    if (leftmost !== null && leftmost <= asideRight - FULL_WIDTH_INTRUSION_PX) {
      return node;
    }
    node = node.nextElementSibling as HTMLElement | null;
  }
  return null;
}
